import type { VaultRecord } from './crypto';

/**
 * Where the private vault's metadata lives.
 *
 * The record holds a salt and a wrapped key, neither of which is a secret, so
 * plain storage is appropriate. What it does *not* hold is the passphrase or
 * anything derived from it that could be tested offline without paying the
 * full key derivation cost.
 */
const VAULT_STORAGE_KEY = 'cinesync.vault';

export function loadVaultRecord(): VaultRecord | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(VAULT_STORAGE_KEY);
    if (raw === null) return null;

    const parsed: unknown = JSON.parse(raw);
    return isVaultRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveVaultRecord(record: VaultRecord): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(VAULT_STORAGE_KEY, JSON.stringify(record));
}

/**
 * Forget the vault.
 *
 * This does not delete the encrypted data, but without the wrapped key that
 * data can never be read again by anyone, including its owner. Callers must
 * treat it as destructive and say so.
 */
export function forgetVaultRecord(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(VAULT_STORAGE_KEY);
}

export function hasVault(): boolean {
  return loadVaultRecord() !== null;
}

/** Structural check, because storage is untrusted input like anything else. */
function isVaultRecord(value: unknown): value is VaultRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  const wrapped = record['wrappedKey'] as Record<string, unknown> | undefined;

  return (
    record['version'] === 1 &&
    typeof record['salt'] === 'string' &&
    typeof record['iterations'] === 'number' &&
    record['iterations'] > 0 &&
    typeof wrapped === 'object' &&
    wrapped !== null &&
    typeof wrapped['iv'] === 'string' &&
    typeof wrapped['data'] === 'string'
  );
}
